import type { SortOrder } from '@/data-viz/data-source.interface';
import { UniverseId } from '@/domain/universes/universe.interface';
import type { BuildingId } from '@/interfaces/Building.interface';
import type { UnzonedDate, UnzonedDateTime } from '@/interfaces/Date.interface';
import type { SensorTimeseriesId } from '@/interfaces/Timeseries.interface';
import { APIError } from '@/providers/errors.constants';
import { InformationProvider } from '@/providers/information/information.provider';
import { AdvizeoModalService } from '@/services/advizeo-modal.service';
import { type ColorPalette, designSystemColorPalettes, SharedUiColorNameV2 } from '@/shared/ui/theming';
import { PaginatedResource } from '@/shared/utils/angular-sdk';
import { ButtonStyle } from '@/ui/button/ui-button';
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, type Observable, of, Subject } from 'rxjs';
import {
    type AlertScenarioId,
    AlertScenarioType,
    type AlertSortableColumn,
    type AlertStatPeriodCode,
    type AlertValidation
} from '../../alerts.interface';
import { AdvizeoCoreAlertScenariosService } from '../../services/advizeo-core-alert-scenarios.service';
import {
    type DriftAlertsAnalysis,
    type DriftAlertScenarioCreation,
    DriftAlertType,
    type DriftGlobalStats,
    DriftMeasureLevel,
    type DriftScenario,
    type DriftScenarioBuildingLevelAbruptStat,
    type DriftScenarioBuildingLevelGradualStat,
    type DriftScenarioFilters,
    type DriftScenarioSenLevelAbruptStat,
    type DriftScenarioSenLevelGradualStat,
    type DriftScenarioSensorStat,
    type DriftScenarioSummary,
    type DriftScenarioUpdate,
    DriftSensitivityLevel,
    DriftTargetMeasure
} from '../drift-scenario.interface';
import { AdvizeoCoreDriftScenarioService } from './advizeo-core-drift-scenario.service';

@Injectable({
    providedIn: 'root'
})
export class DriftScenarioService {
    private _advizeoCoreDriftScenarioProvider = inject(AdvizeoCoreDriftScenarioService);
    private _advizeoCoreAlertScenariosService = inject(AdvizeoCoreAlertScenariosService);
    private _informationProvider = inject(InformationProvider);
    private _translateService = inject(TranslateService);
    private _modalService = inject(AdvizeoModalService);

    private _alertsScenariosListUpdate$ = new Subject<void>();

    getAlertsScenarios(): PaginatedResource<
        DriftScenarioSummary,
        { periodCode: AlertStatPeriodCode; viewAll: boolean; filters: DriftScenarioFilters }
    > {
        return new PaginatedResource(
            ({ periodCode, viewAll, filters }, page, pageSize) =>
                firstValueFrom(
                    this._advizeoCoreDriftScenarioProvider.fetchAlertScenarios$({ page, size: pageSize }, periodCode, viewAll, filters)
                ),
            {
                dataChanged$: this._alertsScenariosListUpdate$
            }
        );
    }

    createDriftAlertScenario(driftScenarioCreation: DriftAlertScenarioCreation): Promise<AlertScenarioId> {
        return this._advizeoCoreDriftScenarioProvider.createDriftAlertScenario(driftScenarioCreation).then(id => {
            this._alertsScenariosListUpdate$.next();
            return id;
        });
    }

    getAvailableDriftUniverseIds$(): Observable<UniverseId[]> {
        return of([UniverseId.ELECTRICITY]);
    }

    getAvailableTargetMeasures$(): Observable<DriftTargetMeasure[]> {
        return of([DriftTargetMeasure.NIGHT_SETBACK, DriftTargetMeasure.BASE_PEAK_LOAD_RATIO]);
    }

    getTargetMeasureColorPalette(targetMeasure: DriftTargetMeasure): ColorPalette {
        switch (targetMeasure) {
            case DriftTargetMeasure.NIGHT_SETBACK:
                return designSystemColorPalettes['category-3'];
            case DriftTargetMeasure.BASE_PEAK_LOAD_RATIO:
                return designSystemColorPalettes['category-4'];
        }
    }

    getAvailableMeasureLevels$(): Observable<DriftMeasureLevel[]> {
        return of([DriftMeasureLevel.BUILDING, DriftMeasureLevel.SEN]);
    }

    getAvailableSensitivityLevels$(): Observable<DriftSensitivityLevel[]> {
        return of([DriftSensitivityLevel.HIGH, DriftSensitivityLevel.MEDIUM, DriftSensitivityLevel.LOW]);
    }

    extractErrorsFromApiFailure(errorCode: APIError, payload: APIError[]): APIError[] {
        if (errorCode === APIError.INVALID_CONFIGURATION) {
            return payload;
        } else {
            return [errorCode];
        }
    }

    showErrors(errors: APIError[]) {
        if (errors.length === 1) {
            switch (errors[0]) {
                case APIError.ALREADY_USED_NAME:
                    this._informationProvider.showToast(
                        this._translateService.instant('PAGES.ALERT_SCENARIO.ERRORS.ALREADY_USED_NAME'),
                        'error'
                    );
                    break;
                case APIError.INVALID_DATE_RANGE:
                    this._informationProvider.showToast(
                        this._translateService.instant('PAGES.ALERT_SCENARIO.ERRORS.INVALID_DATE_RANGE'),
                        'error'
                    );
                    break;

                default:
                    this._informationProvider.showToast(
                        this._translateService.instant('PAGES.ALERT_SCENARIO.ERRORS.UNKNOWN_SINGLE_ERROR'),
                        'error'
                    );
                    break;
            }
        } else {
            this._informationProvider.showToast(this._translateService.instant('PAGES.ALERT_SCENARIO.ERRORS.MULTIPLE_ERRORS'), 'error');
        }
    }

    getDriftScenario$(scenarioId: AlertScenarioId): Observable<DriftScenario> {
        return this._advizeoCoreDriftScenarioProvider.getDriftScenario$(scenarioId);
    }

    setAlertScenarioEnabled(alertScenarioId: AlertScenarioId, enabled: boolean): Promise<void> {
        return this._advizeoCoreAlertScenariosService
            .setAlertScenarioEnabled(alertScenarioId, AlertScenarioType.DRIFT, enabled)
            .then(() => this._alertsScenariosListUpdate$.next());
    }

    requestAlertScenarioDeletion(alertScenarioId: AlertScenarioId): Promise<void> {
        return this._modalService
            .openDialog({
                title: this._translateService.instant('PAGES.ALERT_SCENARIO.DELETE_SCENARIO.TITLE'),
                message: this._translateService.instant('PAGES.ALERT_SCENARIO.DELETE_SCENARIO.DESCRIPTION'),
                buttons: [
                    {
                        text: this._translateService.instant('BUTTONS.VALIDATE'),
                        onClick: () => {
                            this.deleteAlertScenario(alertScenarioId)
                                .then(() => {
                                    this._informationProvider.showToast(
                                        this._translateService.instant('PAGES.ALERT_SCENARIO.DELETE_SCENARIO.SUCCESS'),
                                        'success'
                                    );
                                })
                                .catch(() => {
                                    this._informationProvider.showToast(
                                        this._translateService.instant('PAGES.ALERT_SCENARIO.DELETE_SCENARIO.ERROR'),
                                        'error'
                                    );
                                });
                        },
                        color: SharedUiColorNameV2.PRIMARY
                    },
                    {
                        text: this._translateService.instant('BUTTONS.CANCEL'),
                        onClick: () => {},
                        color: SharedUiColorNameV2.PRIMARY,
                        buttonStyle: ButtonStyle.OUTLINE
                    }
                ]
            })
            .afterClosed()
            .then();
    }

    private deleteAlertScenario(alertScenarioId: AlertScenarioId): Promise<void> {
        return this._advizeoCoreAlertScenariosService
            .deleteAlertScenario(alertScenarioId)
            .then(() => this._alertsScenariosListUpdate$.next());
    }

    updateDriftScenario(alertScenarioId: AlertScenarioId, scenarioUpdate: DriftScenarioUpdate): Promise<void> {
        return this._advizeoCoreDriftScenarioProvider.updateDriftScenario(alertScenarioId, scenarioUpdate);
    }

    saveAlertScenario(alertScenarioId: AlertScenarioId): Promise<void> {
        return this._advizeoCoreDriftScenarioProvider.saveAlertScenario(alertScenarioId);
    }

    publishAlertScenario(alertScenarioId: AlertScenarioId): Promise<void> {
        return this._advizeoCoreDriftScenarioProvider.publishAlertScenario(alertScenarioId);
    }

    getScenarioValidation$(alertScenarioId: AlertScenarioId): Observable<AlertValidation> {
        return this._advizeoCoreDriftScenarioProvider.getAlertScenarioValidation$(alertScenarioId);
    }

    getSensorAnalysis$(
        alertScenarioId: AlertScenarioId,
        timeseriesId: SensorTimeseriesId,
        startDate: UnzonedDate,
        endDate: UnzonedDate
    ): Observable<DriftAlertsAnalysis> {
        return this._advizeoCoreDriftScenarioProvider.getSensorAnalysis$(alertScenarioId, timeseriesId, startDate, endDate);
    }

    getBuildingAnalysis$(
        alertScenarioId: AlertScenarioId,
        buildingId: BuildingId,
        startDate: UnzonedDate,
        endDate: UnzonedDate
    ): Observable<DriftAlertsAnalysis> {
        return this._advizeoCoreDriftScenarioProvider.getBuildingAnalysis$(alertScenarioId, buildingId, startDate, endDate);
    }

    getAlertScenarioBuildingsStats(
        alertScenarioId: AlertScenarioId,
        measureLevel: DriftMeasureLevel,
        alertType: DriftAlertType
    ): PaginatedResource<
        | DriftScenarioBuildingLevelAbruptStat
        | DriftScenarioBuildingLevelGradualStat
        | DriftScenarioSenLevelAbruptStat
        | DriftScenarioSenLevelGradualStat,
        {
            periodCode: AlertStatPeriodCode;
            search: string;
            sort?:
                | {
                      name: AlertSortableColumn;
                      direction: SortOrder;
                  }[]
                | null;
        }
    > {
        switch (true) {
            case DriftMeasureLevel.BUILDING === measureLevel && DriftAlertType.GRADUAL === alertType: {
                return new PaginatedResource(({ periodCode, search, sort }, page, pageSize) =>
                    firstValueFrom(
                        this._advizeoCoreDriftScenarioProvider.fetchGradualAlertScenarioBuildingsStatsForBuildingLevel$(
                            { page, size: pageSize, sort },
                            alertScenarioId,
                            periodCode,
                            search
                        )
                    )
                );
            }
            case DriftMeasureLevel.BUILDING === measureLevel && DriftAlertType.ABRUPT === alertType: {
                return new PaginatedResource(({ periodCode, search, sort }, page, pageSize) =>
                    firstValueFrom(
                        this._advizeoCoreDriftScenarioProvider.fetchAbruptAlertScenarioBuildingsStatsForBuildingLevel$(
                            { page, size: pageSize, sort },
                            alertScenarioId,
                            periodCode,
                            search
                        )
                    )
                );
            }
            case DriftMeasureLevel.SEN === measureLevel && DriftAlertType.GRADUAL === alertType: {
                return new PaginatedResource(({ periodCode, search, sort }, page, pageSize) =>
                    firstValueFrom(
                        this._advizeoCoreDriftScenarioProvider.fetchGradualAlertScenarioBuildingsStatsForSenLevel$(
                            { page, size: pageSize, sort },
                            alertScenarioId,
                            periodCode,
                            search
                        )
                    )
                );
            }
            case DriftMeasureLevel.SEN === measureLevel && DriftAlertType.ABRUPT === alertType: {
                return new PaginatedResource(({ periodCode, search, sort }, page, pageSize) =>
                    firstValueFrom(
                        this._advizeoCoreDriftScenarioProvider.fetchAbruptAlertScenarioBuildingsStatsForSenLevel$(
                            { page, size: pageSize, sort },
                            alertScenarioId,
                            periodCode,
                            search
                        )
                    )
                );
            }
            default: {
                throw new Error(`unknow measure level or alert type :${measureLevel}  ${alertType}`);
            }
        }
    }

    getAlertScenarioSensorsStats$(
        alertScenarioId: AlertScenarioId,
        buildingId: BuildingId,
        startDate: UnzonedDateTime,
        endDate: UnzonedDateTime
    ): Observable<DriftScenarioSensorStat[]> {
        return this._advizeoCoreDriftScenarioProvider.fetchAlertScenarioSensorStats$(alertScenarioId, buildingId, startDate, endDate);
    }

    getDriftGlobalStats$(periodCode: AlertStatPeriodCode): Observable<DriftGlobalStats> {
        return this._advizeoCoreDriftScenarioProvider.getDriftGlobalStats$(periodCode);
    }

    getAlertScenarioSummary$(alertScenarioId: AlertScenarioId, periodCode: AlertStatPeriodCode): Observable<DriftScenarioSummary> {
        return this._advizeoCoreDriftScenarioProvider.getAlertScenarioSummary$(alertScenarioId, periodCode);
    }
}
